<script setup lang="ts">
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";
import { Loader2 } from "@lucide/vue";

import { useProject } from "@/stores/project/project.store";
import { useProjectWrapper } from "@/dataProviders/ProjectWrapper";
import { useDashboard } from "@/features/Dashboard";
import { services as BuildingProjectServices } from "@/features/BuildingProject";
import { BuildingProjectStageEnums } from "@/enums";
import { useModalStore } from "@/stores/modals/modals.store";
import SelectInputNew from "@/components/Reusables/Inputs/SelectInputNew.vue";

// Dialog for changing the project's stage/status (buildingProjectStageEnum), opened from the pencil on
// the dashboard context strip's Status fact (RTLCA-5216). It was an inline select + Save + cancel in the
// strip itself, which broke that row's layout: the strip is a line of short label/value pairs, and a
// 32px control with two buttons beside it does not belong there.
// Picking "Archived" is how a user marks a finished project completed (RTLCA-4845) — there is no
// separate "Completed" value.
const toast = useToast();
const dashboard = useDashboard();
const projectStore = useProject();
const projectWrapper = useProjectWrapper();
const { project } = storeToRefs(projectStore);
const { closeByName } = useModalStore();

const stageOptions = BuildingProjectStageEnums.getAsArray().map((stage) => ({ name: stage.key, value: stage.value }));

const currentStage = computed<number | null>(() => project.value?.buildingProjectStageEnum ?? null);

// Held as a draft and only persisted on Save, so cancelling leaves the project untouched.
const draft = ref<number | null>(currentStage.value);
const isSaving = ref(false);

const hasChange = computed(() => draft.value !== null && draft.value !== currentStage.value);

const cancel = () => closeByName("ProjectStatusModal", { action: "cancel" });

const save = async () => {
  const current = project.value;
  if (!current || draft.value === null || !hasChange.value) {
    cancel();
    return;
  }

  const nextStage = draft.value;
  isSaving.value = true;
  try {
    await BuildingProjectServices.updateProjectStage(current, nextStage, {
      statusLabel: BuildingProjectStageEnums.getKeyFromValue(nextStage),
      previousStatusLabel: currentStage.value === null ? null : BuildingProjectStageEnums.getKeyFromValue(currentStage.value),
    });
    // Re-read the project into the settings page's wrapper (Hasura) and hand the result to this store, so
    // both surfaces show the new status without a reload (RTLCA-5645). The dashboard's cached project data
    // (the exports and other readers use it) is patched in place below.
    await projectWrapper.refreshProjectWrapper(current.id, true, "hasura");
    projectStore.applySavedProject(projectWrapper.getProjectWrapper().project);
    if (dashboard.projectData) {
      dashboard.projectData.stage = BuildingProjectStageEnums.getKeyFromValue(nextStage as number);
    }
    toast.success("Project status updated");
    closeByName("ProjectStatusModal", { action: "saved", stage: nextStage });
  } catch (error: unknown) {
    Sentry.captureException(error);
    toast.error("Could not update project status. Please try again.");
  } finally {
    isSaving.value = false;
  }
};

const btnBase =
  "focus-ring interactive-active t-small-bold duration-fast ease-standard inline-flex items-center justify-center gap-2 rounded-8 px-4 py-2 transition-colors";
</script>

<template>
  <div class="flex flex-col gap-4">
    <h2 class="t-sh4 text-type-default">Project status</h2>

    <SelectInputNew
      v-model="draft"
      cast="number"
      label="Status"
      :disabled="isSaving">
      <option
        v-for="option in stageOptions"
        :key="option.value"
        :value="option.value">
        {{ option.name }}
      </option>
    </SelectInputNew>

    <div class="gap-px-8 flex justify-end">
      <button
        type="button"
        :disabled="isSaving"
        :class="[
          btnBase,
          'bg-slate-0 text-type-default border border-slate-200 hover:bg-slate-100',
          { 'interactive-disabled': isSaving },
        ]"
        @click="cancel">
        Cancel
      </button>
      <button
        type="button"
        :disabled="!hasChange || isSaving"
        :class="[
          btnBase,
          hasChange && !isSaving
            ? 'bg-app-blue-600 dark:bg-app-blue-fill hover:bg-app-blue-700 dark:hover:bg-app-blue-fill-hover text-white'
            : 'text-type-muted bg-slate-200',
          { 'interactive-disabled': !hasChange || isSaving },
        ]"
        @click="save">
        <Loader2
          v-if="isSaving"
          class="icon-sm animate-spin"
          :stroke-width="1.5" />
        {{ isSaving ? "Saving…" : "Save" }}
      </button>
    </div>
  </div>
</template>
